import { HttpContext, HttpContextToken } from '@angular/common/http';
import { Observable } from 'rxjs';
import { TokenPair } from './models';
import { AuthService } from 'src/app/shared/auth-m/core/auth.service';
import { EndUserAuthService } from 'src/app/shared/auth-m/core/end-user-auth.service';
import { InvitedAuthService } from 'src/app/shared/auth-m/core/invited-auth.service';
import { inject } from '@angular/core';

/** The Auth M session a request authenticates with. */
export type AuthSessionKind = 'admin' | 'invited' | 'end_user';

/**
 * Declares which session a request belongs to, so the interceptor attaches —
 * and refreshes — the right token.
 *
 * It can't be inferred from the URL: admin management endpoints share the
 * invited and end-user prefixes (`/auth/invited/users` is an ADMIN route).
 * Unmarked requests keep the admin session, as they always have.
 */
export const AUTH_SESSION = new HttpContextToken<AuthSessionKind>(() => {
  const admin = inject(AuthService);
  if(admin.user()) return 'admin';

  const endUser = inject(EndUserAuthService);
  if(endUser.user()) return 'end_user';
  
  const invited = inject(InvitedAuthService);
  if(invited.user()) return 'invited';

  return 'admin';
});

export function withSession(kind: AuthSessionKind): HttpContext {
  return new HttpContext().set(AUTH_SESSION, kind);
}

/** What the interceptor needs from a session to attach, refresh and end it. */
export interface RefreshableSession {
  readonly tokens: TokenPair | null;
  refresh(): Observable<TokenPair>;
  /** The refresh token itself is dead — the session is over. */
  sessionExpired(): void;
  /**
   * Another browser or device signed into the account, which replaced this
   * tab's session. End it and tell the user why (see SIGNED_IN_ELSEWHERE).
   */
  sessionRevoked(): void;
}

/**
 * A 401 "session_revoked": a sign-in elsewhere replaced the session. The
 * gateway puts the code in `error`, Auth M in `detail`.
 */
export function isSessionRevoked(err: unknown): boolean {
  const body = (err as { error?: { error?: unknown; detail?: unknown } } | null)?.error;
  return body?.error === 'session_revoked' || body?.detail === 'session_revoked';
}

/** The login pages show this when a session ends because of a sign-in elsewhere. */
export const SIGNED_IN_ELSEWHERE =
  'You were signed out because your account was signed in on another device or browser.';

/** Navigation state the login pages look for (`history.state.signedInElsewhere`). */
export const SIGNED_IN_ELSEWHERE_STATE = { signedInElsewhere: true };

/**
 * The end-user sign-out event for this reason. End users sign in through a
 * dialog, which the public header opens when it hears it.
 */
export const SIGNED_IN_ELSEWHERE_REASON = 'signed_in_elsewhere';
